import { Slide, TableCell } from '@features/powerpoint';
import { contentSlideHeader, generateTable } from '../../../../utils';
import { formatters } from '@utils/formattersV2';
import {
  Lci3Report,
  Lci3SalesMetrics,
} from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { unifiedPlatformColorPalette } from '@utils/unifiedPlatformColorPalette';
import { Subdimension } from '@features/reports/subdomains/visitation';

export const TABLE_HEADER_OPTIONS = {
  color: unifiedPlatformColorPalette.inmarketGreen,
  bold: true,
};

type SummaryTableSlideProps = {
  slideMaster: string;
  subdimensions: Subdimension[];
  overallSalesMetrics: Lci3SalesMetrics;
  slideHeader: string;
  overallVisitMetrics: Lci3Report['visitMetrics'];
};

const rsaPerformerColumnLabels = [
  {
    text: 'Dimension',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Total Impressions',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Unique Users Reached',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Incremental Sales Lift',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Incremental Sales',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Transaction Size Lift',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Transaction Rate Lift',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Penetration Lift',
    options: TABLE_HEADER_OPTIONS,
  },
] as const satisfies TableCell[];

const formatMetrics = (
  salesMetrics: Lci3SalesMetrics | null,
  visitMetrics: Lci3Report['visitMetrics'] | Subdimension['visitMetrics'],
  value: string
): TableCell[] => {
  const incrementalSalesLift = formatters.lift(
    salesMetrics?.incrementalSalesLift.total
  );

  const incrementalSales = formatters.currency(
    salesMetrics?.incrementalSales.total,
    { full: true }
  );

  const transactionSizeLift = formatters.percentage(
    salesMetrics?.transactionSizeLift.total,
    { decimals: 1 }
  );

  const transactionRateLift = formatters.percentage(
    salesMetrics?.transactionRateLift.total,
    { decimals: 1 }
  );

  const penetrationLift = formatters.percentage(
    salesMetrics?.penetrationLift.total,
    { decimals: 1 }
  );

  const uniqueUsersReached = formatters.users(visitMetrics.uniqueUsers, {
    full: true,
  });

  const totalImpressions = formatters.impressions(visitMetrics.impressions, {
    full: true,
  });

  return [
    { text: value },
    { text: totalImpressions },
    { text: uniqueUsersReached },
    { text: incrementalSalesLift },
    { text: incrementalSales },
    { text: transactionSizeLift },
    { text: transactionRateLift },
    { text: penetrationLift },
  ];
};

const subdimensionRows = (
  subdimensions: Subdimension[],
  overallSalesMetrics: Lci3SalesMetrics,
  overallVisitMetrics: Lci3Report['visitMetrics']
): TableCell[][] => {
  const overallVisitMetricsRowData = formatMetrics(
    overallSalesMetrics,
    overallVisitMetrics,
    'Overall'
  );
  const subdimensionRowData = subdimensions.map(subdimension =>
    formatMetrics(
      subdimension.salesMetrics,
      subdimension.visitMetrics,
      subdimension.value
    )
  );

  return [overallVisitMetricsRowData, ...subdimensionRowData];
};

export const rsaDimensionsTableSlide = (
  props: SummaryTableSlideProps
): Slide => {
  return {
    slideMaster: props.slideMaster,
    tables: [
      generateTable(
        rsaPerformerColumnLabels,
        subdimensionRows(
          props.subdimensions,
          props.overallSalesMetrics,
          props.overallVisitMetrics
        )
      ),
    ],
    texts: [contentSlideHeader(props.slideHeader)],
  };
};
